export interface MemberToAdd {
  name: string;
  email: string;
  role: string;
  valid: boolean | null;
}

export interface Team {
  ID: number;
  EnterpriseID: number;
  Name: string;
  DefaultLicenseID: number;
  UnlockLessons: boolean;
  JourneyEnabled: boolean;
  Code?: string;
  role?: string;
}

export interface LearnerTeam {
  id: number;
  name: string;
}

export interface License {
  ID: number;
  Name: string;
  StartDate?: Date;
  EndDate?: Date;
  Duration?: number;
  PurchasedSeats?: number;
}

export interface TeamLesson {
  ID: number;
  TeamID: number | null;
  Title: string;
  IntroText: string;
  Img: string;
  ShortBlurb: null | string;
  Order: number;
  Active: boolean;
}

export interface UserMeta {
  Language: string | null;
  Gender: string | null;
  Profession: string | null;
  Name: string | null;
}

export interface TeamEngagement {
  ActiveUsers: number | null;
  ActiveUserNames: string[] | null;
  RareUsers: number | null;
  RareUserNames: string[] | null;
  AbsentUsers: number | null;
  AbsentUserNames: string[] | null;
}

export interface TeamCompletion {
  noUsersAdded: boolean;
  lessons: TeamCompletionLessons[] | null;
}

export interface TeamCompletionLessons {
  lessonID: number | null;
  title: string | null;
  completed: number | null;
  total: number | null;
  completedBy: string[] | null;
}

export interface TeamLeaderboard {
  Top: Leaderboard[] | null;
  Bottom: Leaderboard[] | null;
}

export interface Leaderboard {
  UID: string | null;
  Name: string | null;
  Level: number | null;
  XP: number | null;
  LastActive: Date | null;
  CompletedLessonCount: number | null;
  TotalLessonCount: number | null;
  AvgScore: number | null;
}

export interface UserSessionResult {
  SessionDetails: SessionResult | null;
  Feedback: SessionFeedback[] | null;
}

export interface SessionResult {
  UserName: string | null;
  Title: string | null;
  AudioFileURL: string | null;
  VideoFileURL: string | null;
  Pacing: number | null;
  Energy: number | null;
  Clarity: number | null;
  Transcript: string[] | null;
}

export interface SessionFeedback {
  ID: number | null;
  StartIndex: number | null;
  EndIndex: number | null;
  Comments: string | null;
  CreatedOn: Date | null;
  LastEdited: Date | null;
  ManagerName: string | null;
}

export interface TeamPerformance {
  Lessons: string[] | null;
  Scores: number[] | null;
}

export interface User {
  Name: string;
  Email: string;
  AcceptedOn: Date | null;
  Role: Role;
  UID: string;
  ID: number;
  CurrentLicenseID: number;
  Expired: boolean;
  AddedBy: null | string;
}

export interface UserSelf {
  doneJourneyOnboarding: boolean;
  email: string;
  uid: string;
  name: string;
  provider: string;
}

export interface InitAssessmentPost {
  skillToImprove: string;
  mainAudience: string;
  givingPresentations: number;
  speakNoPractice: number;
}

export enum Role {
  EnterpriseAdmin = "enterprise_admin",
  Manager = "manager",
  Reviewer = "reviewer",
  User = "user",
}

export enum ConfigureReviewerEnum {
  Freeform = "freeform",
  Custom = "custom",
  Empty = "",
}

export interface LearnerReviews {
  sid: string;
  title: string;
  reviewedOn: Date;
}

export interface RecordInitiator {
  Type: string;
  ID: Number;
}

export interface UserStat {
  Clarity: number;
  Filler: number;
  LastSessionDate: Date; // "2018-08-18T00:20:14.648376Z"
  Name: string;
  OverallScore: number;
  Pace: number;
  SessionsCount: number;
  TotalDuration: number;
  UID: string;
}

export interface TeamActivity {
  uid: string;
  name: number;
  lastRecordingDate: Date; // "2018-08-18T00:20:14.648376Z"
  averageScore: number;
}

export interface TeamLessonsScores {
  lessontitle: string;
  lessonID: number;
  concisenessScore: number;
  confidenceScore: number;
  paceScore: number;
  fillerScore: number;
}

export interface Answer {
  answer: string;
  displayOrder: number;
}

export interface Question {
  question: string;
  displayOrder: number;
  answers: Answer[];
  selectedAnswer: number;
}
export interface RubricSession {
  rubric: Rubric;
  sessionAnswers: RubricSessionAnswers[];
  answersByUser: RubricAnswersByUser[];
  averageAnswers: RubricAnswers[];
}
export interface RubricSessionAnswers {
  answeredBy: string;
  answeredByName: string;
  answeredOn: Date;
  question: Question;
  starsRating: number;
}
export interface RubricAnswersByUser {
  uid: string;
  name: string;
  answers: RubricAnswers[];
}
export interface RubricAnswers {
  question: RubricQuestionAnswer;
  starsRating: number;
}
export interface RubricQuestionAnswer {
  displayOrder: number;
}
export interface Rubric {
  name: string;
  rubricSlug: string;
  locked: boolean;
  linked: boolean;
  version: string;
  questions: Question[];
}
export interface RubricUpdate {
  version: string;
}
export interface CreateRubric {
  rubricSlug: string;
  version: string;
}
export interface TeamReviewSettings {
  method: ConfigureReviewerEnum;
  teamUsers: TeamUsers[];
  reviewers: TeamReviewers[];
  version: string;
}
export interface TeamUsers {
  uid: string;
  name: string;
  isReviewer: boolean;
  role: Role;
}

export interface TeamReviewers {
  uid: string;
  name: string;
  reviewees: TeamReviewees[];
}

export interface TeamReviewees {
  uid: string;
  name: string;
}

export interface MyExercise {
  ID: number;
  RichModuleText: string;
  FeedbackOptions: FeedbackOptions;
}

export interface FeedbackOptions {
  Filler: boolean;
  Pace: boolean;
  Energy: boolean;
  Clarity: boolean;
  Pause: boolean;
  Conciseness: boolean;
  Confidence: boolean;
  Video: boolean;
}

export interface VideoCallBackData {
  Image?: Blob;
  Video?: Blob;
}

export interface UserPlan {
  id: string;
  name: string;
  enterprise: boolean;
  hasUsedUpFreeTrial: boolean;
  platform: string;
  displayStripeManagementButton: boolean;
}

export interface License {
  LicenseKey: string;
  UsedBy: string;
  IsOwner: boolean;
}

export interface PlanBulletPoint {
  enabled: boolean;
  text: string;
}

export interface PlanStripePricing {
  monthlyPrice: string;
  yearlyPrice: string;
  lifetimePrice: string;
}

export interface AvailablePlan {
  id: string;
  name: string;
  enterprise: boolean;
  bulletPoints: PlanBulletPoint[];
  stripePricing: PlanStripePricing;
}

export interface Review {
  sid: string;
  userName: string;
  title: string;
  submittedOn: Date;
  reviewedOn: Date;
}

export enum ReviewType {
  ReviewRequired = "review_required",
  AlreadyReviewed = "already_reviewed",
}
